import area from '@turf/area';
import centroid from '@turf/centroid';
import type { Feature, Polygon, Position } from 'geojson';

/**
 * Geometry helpers around Turf.
 *
 * Turf works in GeoJSON coordinate order — [lng, lat] — while Leaflet works in
 * [lat, lng]. Everything crossing that boundary goes through here so the swap
 * happens in exactly one place.
 */

/** Turf's area is on a sphere and returns m² for any polygon on Earth. */
export function computeAreaSqm(feature: Feature<Polygon>): number {
  return area(feature);
}

/** Centroid as Leaflet-order [lat, lng]. */
export function computeCentroidLatLng(feature: Feature<Polygon>): [number, number] {
  const c = centroid(feature);
  const [lng, lat] = c.geometry.coordinates as [number, number];
  return [lat, lng];
}

/** Build a GeoJSON Polygon Feature from a Leaflet ring of [lat, lng] pairs. */
export function polygonFromLatLngs(latlngs: Array<{ lat: number; lng: number }>): Feature<Polygon> | null {
  if (latlngs.length < 3) return null;

  const ring: Position[] = latlngs.map(({ lat, lng }) => [lng, lat]);

  // GeoJSON requires the exterior ring to be explicitly closed; Leaflet's ring
  // is implicitly closed, so repeat the first vertex.
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (!first || !last) return null;

  const [firstLng, firstLat] = first as [number, number];
  const [lastLng, lastLat] = last as [number, number];
  if (firstLng !== lastLng || firstLat !== lastLat) {
    ring.push([firstLng, firstLat]);
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

const SQM_FORMATTER = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 });
const HA_FORMATTER = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "4.512" — Romanian grouping, no decimals; square metres are never fractional in practice. */
export function formatSqm(value: number): string {
  return SQM_FORMATTER.format(Math.round(value));
}

/** "0,45" — Romanian decimal comma. */
export function formatHa(sqm: number): string {
  return HA_FORMATTER.format(sqm / 10_000);
}

/** Romania's bounding box, used to keep the map and geocoder on-country. */
export const ROMANIA_BOUNDS: [[number, number], [number, number]] = [
  [43.6, 20.2],
  [48.3, 30.0],
];

export const ROMANIA_CENTER: [number, number] = [45.9432, 24.9668];
export const ROMANIA_DEFAULT_ZOOM = 7;
